import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ShieldCheck } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, TextInput } from "@/components/site/Field";
import { IMAGES } from "@/lib/data";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const { setFromToken } = useAuth();
  const [info, setInfo] = useState(null); // null=loading, false=invalid, obj=ok
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setInfo(false); return; }
    api.get(`/auth/invite-info?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setInfo(data))
      .catch((err) => { setInfo(false); toast.error(apiErrorMessage(err)); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords don't match.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, password });
      setFromToken(data.user);
      toast.success("Welcome to the team!");
      nav("/admin");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={IMAGES.logo} alt="Harbour Haze" className="h-16 w-16 mx-auto object-contain" />
          <h1 className="mt-4 font-head font-black text-3xl tracking-tight">Join the team</h1>
          <p className="overline text-foreground/50 mt-1">Harbour Haze Admin</p>
        </div>

        {info === null && <p className="text-center font-mono-plex text-sm text-foreground/50">Checking your invite…</p>}

        {info === false && (
          <div className="bg-background border-2 border-foreground rounded-2xl shadow-brutal-lg p-8 text-center">
            <p className="font-head font-bold text-lg">This invite link is invalid or expired.</p>
            <p className="text-foreground/60 text-sm mt-2">Ask the owner to send you a fresh invite.</p>
            <a href="/admin/login" className="inline-block mt-5 font-mono-plex text-xs text-foreground/50 hover:text-foreground">← Go to login</a>
          </div>
        )}

        {info && info.email && (
          <form onSubmit={submit} className="bg-background border-2 border-foreground rounded-2xl shadow-brutal-lg p-8 space-y-5" data-testid="accept-invite-form">
            <div className="flex items-center gap-3 bg-secondary/15 border-2 border-foreground rounded-xl px-4 py-3">
              <ShieldCheck size={20} className="text-secondary" />
              <div>
                <p className="font-head font-bold leading-tight">{info.name}</p>
                <p className="font-mono-plex text-xs text-foreground/60">{info.email}</p>
              </div>
            </div>
            <Field label="Create a password"><TextInput data-testid="invite-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" /></Field>
            <Field label="Confirm password"><TextInput data-testid="invite-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat password" /></Field>
            <button type="submit" disabled={loading} data-testid="invite-activate"
              className="w-full font-head font-bold text-lg bg-foreground text-background rounded-full py-4 flex items-center justify-center gap-2 hover:bg-primary hover:text-foreground transition-colors disabled:opacity-60">
              <Lock size={16} /> {loading ? "Activating…" : "Activate account"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
